/**
 * RBAC Service - Frontend API client for role and permission management
 * Communicates with Backend FastAPI RBAC endpoints
 */

import api from "./api";
import {
  Role,
  Permission,
  RoleCreate,
  PermissionCreate,
  UserPermissionsResponse,
  PermissionCheckResponse,
} from "@/types/rbac";

const RBAC_API_BASE = "/rbac";

// Cache keys
const CACHE_KEYS = {
  ROLES: "rbac:roles",
  PERMISSIONS: "rbac:permissions",
  USER_PERMISSIONS: (userId: string) => `rbac:user:${userId}:permissions`,
};

// Simple in-memory cache
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_TTL;
};

const getFromCache = (key: string): any | null => {
  const cached = cache.get(key);
  if (cached && isCacheValid(cached.timestamp)) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setInCache = (key: string, data: any): void => {
  cache.set(key, { data, timestamp: Date.now() });
};

const invalidateCache = (key: string): void => {
  cache.delete(key);
};

const invalidateCachePattern = (pattern: string): void => {
  Array.from(cache.keys())
    .filter((key) => key.includes(pattern))
    .forEach((key) => cache.delete(key));
};

export const rbacService = {
  // ============================================
  // ROLE MANAGEMENT
  // ============================================

  /**
   * Get all available roles (cached)
   */
  getRoles: async (): Promise<Role[]> => {
    try {
      // Check cache first
      const cached = getFromCache(CACHE_KEYS.ROLES);
      if (cached) {
        return cached;
      }

      const response = await api.get<Role[]>(`${RBAC_API_BASE}/roles`);
      setInCache(CACHE_KEYS.ROLES, response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch roles:", error);
      throw error;
    }
  },

  /**
   * Create a new role (invalidates roles cache)
   */
  createRole: async (roleData: RoleCreate): Promise<Role> => {
    try {
      const response = await api.post<Role>(`${RBAC_API_BASE}/roles`, roleData);
      // Invalidate roles cache
      invalidateCache(CACHE_KEYS.ROLES);
      return response.data;
    } catch (error) {
      console.error("Failed to create role:", error);
      throw error;
    }
  },

  /**
   * Update an existing role (invalidates roles cache)
   */
  updateRole: async (roleId: string, roleData: Partial<RoleCreate>): Promise<Role> => {
    try {
      const response = await api.put<Role>(`${RBAC_API_BASE}/roles/${roleId}`, roleData);
      // Invalidate roles cache
      invalidateCache(CACHE_KEYS.ROLES);
      invalidateCachePattern("rbac:user"); // Invalidate user permissions cache
      return response.data;
    } catch (error) {
      console.error("Failed to update role:", error);
      throw error;
    }
  },

  /**
   * Delete a role (invalidates roles cache)
   */
  deleteRole: async (roleId: string): Promise<void> => {
    try {
      await api.delete(`${RBAC_API_BASE}/roles/${roleId}`);
      // Invalidate roles cache
      invalidateCache(CACHE_KEYS.ROLES);
      invalidateCachePattern("rbac:user");
    } catch (error) {
      console.error("Failed to delete role:", error);
      throw error;
    }
  },

  /**
   * Remove a permission from a role (invalidates caches)
   */
  removePermissionFromRole: async (roleId: string, permissionId: string): Promise<void> => {
    try {
      await api.delete(`${RBAC_API_BASE}/roles/${roleId}/permissions/${permissionId}`);
      // Invalidate caches
      invalidateCache(CACHE_KEYS.ROLES);
      invalidateCachePattern("rbac:user");
    } catch (error) {
      console.error("Failed to remove permission from role:", error);
      throw error;
    }
  },

  // ============================================
  // PERMISSION MANAGEMENT
  // ============================================

  /**
   * Get all available permissions (cached)
   */
  getPermissions: async (): Promise<Permission[]> => {
    try {
      // Check cache first
      const cached = getFromCache(CACHE_KEYS.PERMISSIONS);
      if (cached) {
        return cached;
      }

      const response = await api.get<Permission[]>(`${RBAC_API_BASE}/permissions`);
      setInCache(CACHE_KEYS.PERMISSIONS, response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch permissions:", error);
      throw error;
    }
  },

  /**
   * Create a new permission (invalidates permissions cache)
   */
  createPermission: async (permissionData: PermissionCreate): Promise<Permission> => {
    try {
      const response = await api.post<Permission>(`${RBAC_API_BASE}/permissions`, permissionData);
      // Invalidate permissions cache
      invalidateCache(CACHE_KEYS.PERMISSIONS);
      return response.data;
    } catch (error) {
      console.error("Failed to create permission:", error);
      throw error;
    }
  },

  // ============================================
  // USER-ROLE ASSIGNMENTS
  // ============================================

  /**
   * Assign a role to a user
   */
  assignRoleToUser: async (userId: string, roleId: string): Promise<void> => {
    try {
      await api.post(`${RBAC_API_BASE}/users/${userId}/roles`, {
        role_id: roleId,
      });
    } catch (error) {
      console.error("Failed to assign role to user:", error);
      throw error;
    }
  },

  /**
   * Remove a role from a user
   */
  removeRoleFromUser: async (userId: string, roleId: string): Promise<void> => {
    try {
      await api.delete(`${RBAC_API_BASE}/users/${userId}/roles/${roleId}`);
    } catch (error) {
      console.error("Failed to remove role from user:", error);
      throw error;
    }
  },

  // ============================================
  // ROLE-PERMISSION ASSIGNMENTS
  // ============================================

  /**
   * Assign a permission to a role (invalidates user permissions cache)
   */
  assignPermissionToRole: async (roleId: string, permissionId: string): Promise<void> => {
    try {
      await api.post(`${RBAC_API_BASE}/roles/${roleId}/permissions`, {
        permission_id: permissionId,
      });
      // Invalidate all user permissions caches since role permissions changed
      invalidateCachePattern("rbac:user");
    } catch (error) {
      console.error("Failed to assign permission to role:", error);
      throw error;
    }
  },

  // ============================================
  // USER PERMISSIONS QUERIES
  // ============================================

  /**
   * Get all permissions for a specific user (cached)
   */
  getUserPermissions: async (userId: string): Promise<UserPermissionsResponse> => {
    try {
      const cacheKey = CACHE_KEYS.USER_PERMISSIONS(userId);
      // Check cache first
      const cached = getFromCache(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await api.get<UserPermissionsResponse>(
        `${RBAC_API_BASE}/users/${userId}/permissions`
      );
      setInCache(cacheKey, response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to fetch user permissions:", error);
      throw error;
    }
  },

  /**
   * Check if current user has a specific permission
   */
  checkPermission: async (resource: string, action: string): Promise<PermissionCheckResponse> => {
    try {
      const response = await api.post<PermissionCheckResponse>(
        `${RBAC_API_BASE}/permissions/check`,
        {
          resource,
          action,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to check permission:", error);
      throw error;
    }
  },

  /**
   * Check if current user has multiple permissions
   */
  checkPermissions: async (
    permissions: Array<{ resource: string; action: string }>
  ): Promise<boolean> => {
    try {
      const results = await Promise.all(
        permissions.map((p) => rbacService.checkPermission(p.resource, p.action))
      );
      return results.every((r) => r.has_permission);
    } catch (error) {
      console.error("Failed to check permissions:", error);
      return false;
    }
  },
};

export default rbacService;
